import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Box, Button, useTheme, useMediaQuery, LinearProgress, Typography, IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useContext, useEffect, useCallback, isValidElement } from 'react';
import { AuthContext, useApi, API, IconContext } from '@plumeuk/shapeshift-identity';
import { isILibraryCourse, isILibraryGroup } from '@plumeuk/shapeshift-types';
import 'x2js';
import { styled } from '@mui/material/styles';

//Hook returns url path to server resources
//With strapi local dev path is relative
function useFileBaseUrl() {
    const { config } = useContext(AuthContext);
    const baseFileUrl = process.env.NODE_ENV === "production" ? "" : (config?.baseApiUrl ?? "");
    const getUrl = (e) => {
        if (e.startsWith("http"))
            return e;
        return baseFileUrl + e;
    };
    return { baseFileUrl, getUrl };
}

const useStyles$7 = makeStyles()((theme) => ({
    tagContainer: {
        pointerEvents: "none",
        display: "flex"
    },
    featuredTag: {
        backgroundColor: "#FE4A49"
    },
    textWrap: {
        textWrap: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxWidth: "150px",
        justifyContent: "start"
    },
    iconContainer: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
    },
    icon: {
        width: "16px",
        maxHeight: "20px",
        marginRight: "10px",
        "& path": {
            fill: theme.palette.text.primary
        }
    },
    tag: {
        borderRadius: "6px",
        padding: "12px",
        pointerEvents: "none",
        fontSize: "12px",
        height: "20px",
        marginRight: "10px",
        textTransform: "uppercase",
        "& svg": {
            fontSize: "14px",
            paddingRight: "5px"
        }
    }
}));
const LibraryItemTags = ({ className, item, ...props }) => {
    const { classes, cx } = useStyles$7();
    return (jsx(Box, { className: cx(classes.tagContainer, className), ...props, children: item?.tags?.map((e, i) => (jsxs(Button, { "data-test-id": `course-tags-option-${e}`, className: cx(e.title.toLocaleLowerCase() === "featured" ? classes.featuredTag : "", classes.tag), children: [e.icon && jsx(Box, { className: classes.iconContainer, children: jsx("img", { className: classes.icon, src: e.icon }) }), jsx("span", { className: classes.textWrap, children: e.title })] }, `tag-${i}`))) }));
};

const useMq = () => {
    const theme = useTheme();
    const isMobileMq = useMediaQuery(`(max-width:${theme.breakpoints.values.sm - 1}px)`);
    const isTabletMq = useMediaQuery(`(min-width:${theme.breakpoints.values.sm}px) and (max-width:${theme.breakpoints.values.md}px)`);
    const isDesktopMq = useMediaQuery(`(min-width:${theme?.breakpoints.values.md + 1}px)`);
    return {
        mobile: isMobileMq,
        tablet: isTabletMq,
        desktop: isDesktopMq
    };
};

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
const DevError = console.error.bind(null) ;

const useLibrary = (search, data) => {
    const [apiResult, getData, setLibrary] = useApi(data ? null : { url: API.library, data: search }, data);
    const getLibrary = (searchOverride, configOverride) => {
        const data = searchOverride !== undefined ? searchOverride : search;
        getData(configOverride
            ? { ...configOverride, url: API.library, data }
            : { url: API.library, data });
    };
    useEffect(() => data && setLibrary(data), [data]);
    return { items: apiResult.data, apiResult, getLibrary, setLibrary };
};

const useSetCourseEnrollment = () => {
    const [apiResult, getData] = useApi(null);
    const setEnroll = (courseSlug, config) => getData({
        ...(config ?? {}),
        url: API.courseEnroll(courseSlug),
        method: "PUT"
    });
    return { response: apiResult.data, apiResult, setEnroll };
};

const useCourseProgress = (slug, data) => {
    const [apiResult, getData, setProgress] = useApi(data || !slug ? null : API.courseProgress(slug), data);
    const getProgress = (slugOverride, configOverride) => {
        const _slug = slugOverride ?? slug;
        if (!_slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.courseProgress(_slug) }
            : API.courseProgress(_slug));
    };
    useEffect(() => {
        if (data)
            setProgress(data);
    }, [data]);
    return { progress: apiResult.data, apiResult, getProgress, setProgress };
};

const LibraryDataProvider = ({ search, children, data }) => {
    const dataHook = useLibrary(search, data);
    const { getLibrary } = dataHook;
    useEffect(() => {
        if (search !== undefined && !data)
            getLibrary(search);
    }, [search]);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle library items" });
};

makeStyles()((theme) => ({
    searchBox: {
        boxSizing: "border-box",
        borderRadius: "6px",
        marginBottom: "10px",
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        },
        margin: "10px 0"
    }
}));

makeStyles()((theme) => ({
    outlineButton: {
        border: `${theme.palette.secondary.main} 1px solid`,
        color: theme.palette.secondary.main,
        backgroundColor: "transparent",
        "&:hover": {
            backgroundColor: theme.palette.secondary.main + "10"
        }
    }
}));

const useStyles$6 = makeStyles()((theme) => ({
    loadingContainer: {
        display: "flex",
        flexGrow: 2,
        [theme.breakpoints.down("sm")]: {
            padding: "5px 20px",
            flexDirection: "row"
        },
        [theme.breakpoints.up("sm")]: {
            padding: "25px 25px 15px 35px",
            flexDirection: "column",
            justifyContent: "center",
            borderTop: "3px solid #d1d1d150"
        }
    },
    loadingBar: {
        marginBottom: "10px",
        [theme.breakpoints.down("sm")]: {
            flexGrow: 2,
            flexDirection: "row",
            margin: "8px 15px 15px 0"
        }
    },
    percentCompleted: {
        fontSize: "15px"
    }
}));
const CourseLoadingBar = ({ className, percentCompleted, ...props }) => {
    const { classes, cx } = useStyles$6();
    const mq = useMq();
    if (percentCompleted === 100)
        return jsx(Fragment, {});
    return (jsxs(Box, { className: cx(className, classes.loadingContainer), ...props, children: [jsx(LinearProgress, { variant: "determinate", className: classes.loadingBar, value: percentCompleted }), jsxs(Typography, { className: classes.percentCompleted, variant: "body1", children: [jsxs("span", { children: [percentCompleted, "%"] }), !mq.mobile && " complete"] })] }));
};

makeStyles()((theme, { isComplete }) => ({
    courseItem: {
        cursor: isComplete ? "pointer" : "inherit",
        marginBottom: "25px"
    },
    paper: {
        display: "flex",
        borderRadius: "10px",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column"
        }
    },
    imageContainer: {
        overflow: "hidden",
        "& img": {
            objectFit: "cover",
            width: "100%",
            height: "100%",
            borderRadius: "10px 0 0 10px",
            [theme.breakpoints.down("sm")]: {
                borderRadius: "10px 10px 0 0"
            }
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            height: "300px"
        },
        [theme.breakpoints.up("sm")]: {
            width: "250px",
            height: "230px",
            minWidth: "250px",
            maxWidth: "250px"
        }
    },
    infoContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "25px 35px",
        flexGrow: 3,
        "& h4": {
            marginBottom: "15px"
        },
        [theme.breakpoints.down("sm")]: {
            padding: "20px"
        }
    },
    contentContainer: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 3,
        "& > div:first-of-type": {
            display: "flex",
            flexGrow: 2,
            [theme.breakpoints.down("sm")]: {
                flexDirection: "column"
            }
        }
    },
    buttonContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "25px",
        [theme.breakpoints.down("sm")]: {
            padding: "0 20px 20px 20px"
        },
        "& > button": {
            textWrap: "no-wrap",
            margin: "10px 0",
            [theme.breakpoints.down("sm")]: {
                width: "100%",
                margin: "5px 0"
            }
        }
    },
    certificateBtn: {
        backgroundColor: "white",
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        }
    },
    courseNavBtn: {
        paddingRight: "20px",
        "& svg": {
            marginLeft: "6px"
        }
    }
}));

makeStyles()((theme) => ({
    tabs: {
        [theme.breakpoints.down("sm")]: {
            display: "inherit",
            width: "100%"
        }
    },
    tab: {
        textTransform: "uppercase",
        color: theme.palette.text.primary + " !important"
    }
}));

makeStyles()((theme) => ({
    courseTable: {},
    topContainer: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "16px",
        [theme.breakpoints.down("sm")]: {
            display: "inherit"
        }
    },
    title: {
        fontSize: "30px",
        lineHeight: "35px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "18px",
            fontWeight: 400
        }
    },
    noCoursesBody: {
        marginTop: "30px"
    },
    noCoursesForFiltersBody: {
        marginTop: "30px"
    }
}));

const useStyles$5 = makeStyles()((theme) => ({
    libraryItemProgressionActionBar: {
        justifyContent: "space-between",
        background: theme.palette.background.paper,
        cursor: "pointer",
        display: "flex",
        padding: "30px 30px",
        height: "70px",
        borderTop: "1px solid " + theme.palette.grey[200]
    },
    courseItemlink: {
        marginTop: "10px",
        marginRight: "10px"
    },
    iconContainer: {
        justifyContent: "center",
        display: "flex",
        flexDirection: "column"
    },
    leftContainer: {
        flexGrow: 3,
        justifyContent: "center",
        display: "flex",
        flexDirection: "column"
    },
    courseLoadingBar: {
        border: "none !important",
        maxWidth: "50% !important",
        padding: "0 0 0 0 !important"
    }
}));
const LibraryItemProgressionActionBar = ({ item, active }) => {
    const { classes } = useStyles$5();
    const { icon } = useContext(IconContext);
    const { progress, apiResult: progressApiResult, getProgress } = useCourseProgress();
    useEffect(() => {
        if (item.slug && (active || active === undefined) && item.enrolled)
            getProgress(item.slug);
    }, [item.slug, active, item.enrolled]);
    return (jsxs(Box, { className: classes.libraryItemProgressionActionBar, children: [jsx(Box, { className: classes.leftContainer, children: (item.enrolled && isILibraryCourse(item) && progress !== 100)
                    ? ((progress === undefined || progressApiResult.isLoading) ? jsx(LinearProgress, {}) : jsx(CourseLoadingBar, { className: classes.courseLoadingBar, percentCompleted: progress }))
                    : jsx(Typography, { className: classes.courseItemlink, children: (progress === 100) ? "Completed" : "Preview" }) }), jsx(Box, { className: classes.iconContainer, children: icon("arrowForwardIcon", { fontSize: "small" }) })] }));
};

const useStyles$4 = makeStyles()((theme) => ({
    courseItem: {
        cursor: "pointer",
        borderRadius: "10px",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "300px",
        transition: "transform 0.3s ease-out",
        "&:hover": {
            transform: "translate(0, -5px)"
        }
    },
    content: {
        background: theme.palette.background.paper,
        padding: "15px",
        flexGrow: 2
    },
    tagContainer: {
        padding: "15px",
        position: "absolute",
        left: 0,
        right: 0
    },
    imageContainer: {
        height: "180px",
        width: "100%",
        overflow: "hidden",
        "& img": {
            objectFit: "cover",
            width: "100%",
            height: "100%"
        }
    },
    title: {
        fontWeight: 500
    },
    description: {
        color: theme.palette.grey[400],
        marginTop: "15px"
    },
    link: {
        marginRight: "10px"
    },
    actionBar: {
        background: theme.palette.background.paper,
        cursor: "pointer",
        display: "flex",
        padding: "15px",
        borderTop: "1px solid " + theme.palette.grey[200],
        "& svg": {
            marginTop: "3px"
        }
    }
}));
const LibraryItem = ({ className, item, onClick, active, onGoToItemAction, enableProgressionBar, componentOverrides, ...props }) => {
    const { classes, cx } = useStyles$4();
    const { getUrl } = useFileBaseUrl();
    const { icon } = useContext(IconContext);
    const handleOnClick = useCallback((e) => {
        onClick?.(e);
        onGoToItemAction(item);
    }, [onGoToItemAction, onClick, item]);
    let typeClass = "";
    if (item.__type === "plugin::strapi-plugin-shapeshift-cms.course")
        typeClass = "library-item-course";
    if (item.__type === "plugin::strapi-plugin-shapeshift-cms.group")
        typeClass = "library-item-group";
    const getSmallImage = (e) => {
        return e?.formats?.small?.url ?? e.url;
    };
    return (jsxs(Box, { "data-test-id": `course-library-item-${item.slug}`, className: cx(classes.courseItem, className, typeClass), onClick: handleOnClick, ...props, children: [jsx(LibraryItemTags, { className: classes.tagContainer, item: item }), componentOverrides?.imageContainer ?? jsx(Box, { className: classes.imageContainer, children: item.featuredImage && jsx("img", { src: getUrl(getSmallImage(item.featuredImage)) }) }), componentOverrides?.content ?? jsxs(Box, { className: classes.content, children: [jsx(Typography, { className: classes.title, children: item.title }), jsx(Typography, { className: classes.description, children: (item?.description?.length ?? 0) > 120 ?
                            `${item.description?.substring(0, 100)?.trim() ?? ""}...`
                            : item.description })] }), componentOverrides?.actionBar ?? ((enableProgressionBar) ? jsx(LibraryItemProgressionActionBar, { active: active, item: item })
                : jsxs(Box, { className: classes.actionBar, children: [jsx(Typography, { className: classes.link, children: "Preview" }), icon("arrowForwardIcon", { fontSize: "small" })] })), componentOverrides?.footer ?? jsx(Fragment, {})] }));
};

makeStyles()((theme, { hasError, valid }) => ({
    select: {
        padding: "3px 15px",
        height: "43px",
        border: valid ? "1px solid #fff" : "1px solid red",
        boxSizing: "border-box",
        marginBottom: "10px",
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        },
        background: "#fff"
    },
    label: {},
    errorMessage: {
        maxHeight: hasError ? 50 : 0,
        color: "#f30000",
        fontSize: "16px",
        boxSizing: "border-box",
        transition: "max-height 0.15s ease-out",
        "& p": {
            paddingTop: "5px"
        }
    }
}));

makeStyles()((_theme) => ({
    instructorSelect: {}
}));

makeStyles()((theme, { activeTags }) => ({
    tagsBtn: {
        width: "120px",
        margin: "0 20px 0 0",
        border: "1px solid #D8D8D8",
        justifyContent: "space-between",
        background: "white",
        padding: "0 15px",
        fontWeight: 400,
        color: "black",
        borderRadius: "6px",
        "& svg": {
            color: activeTags ? theme.palette.secondary.main : "grey"
        },
        "&:hover": {
            backgroundColor: "white"
        }
    },
    topContainer: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "25px",
        width: "100%"
    },
    clearBtn: {
        color: theme.palette.primary.main,
        cursor: "pointer"
    },
    tagContainer: {},
    tagsFormContainer: {
        padding: "25px",
        width: "200px"
    }
}));

makeStyles()((theme) => ({
    librarySearch: {
        margin: "10px",
        gap: "10px",
        display: "flex",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column"
        },
        "& > *": {
            height: "43px"
        }
    },
    searchBox: {
        margin: "0",
        flexGrow: "2",
        border: "1px solid #D8D8D8"
    },
    select: {
        margin: "0",
        border: "1px solid #D8D8D8",
        borderRadius: "6px"
    }
}));

makeStyles()((theme) => ({
    library: {},
    title: {
        margin: "10px",
        lineHeight: "35px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "35px"
        }
    },
    search: {
        marginTop: "35px"
    },
    itemContainer: {
        padding: "30px 10px"
    },
    libraryItem: {
        width: "100%",
        height: "100%"
    },
    noneFoundContainer: {
        padding: "30px"
    }
}));

makeStyles()((theme) => ({
    carousel: {},
    title: {
        fontWeight: 800,
        margin: "10px",
        fontSize: "34px",
        lineHeight: "35px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "35px"
        }
    },
    itemsContainer: {
        display: "flex",
        gap: "2%",
        padding: "30px 10px",
        overflow: "hidden"
    },
    itemContainer: {
        minWidth: "23.5%",
        maxWidth: "23.5%",
        [theme.breakpoints.down("md")]: {
            minWidth: "100%",
            maxWidth: "100%"
        }
    },
    libraryItem: {
        width: "100%",
        height: "100%",
        maxWidth: "400px",
        borderRadius: "18px",
        border: "1px solid #d1d1d1"
    },
    pageIndicator: {
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        background: "grey",
        marginRight: "10px"
    },
    activePageIndicator: {
        background: theme.palette.secondary.main
    }
}));
styled(IconButton)(() => ({
    color: "white",
    backgroundColor: "grey",
    "&:hover": {
        backgroundColor: "grey"
    },
    "& svg": {
        fontSize: "30px"
    },
    width: "50px",
    height: "50px"
}));

makeStyles()(() => ({
    libraryCarousels: {},
    noneFoundContainer: {
        padding: "30px"
    },
    libraryCarousel: {
        marginBottom: "56px",
        minHeight: "400px"
    }
}));

const useStyles$3 = makeStyles()((_theme) => ({
    title: {
        marginBottom: "30px"
    },
    productContainer: {
        display: "flex",
        flexWrap: "wrap",
        gap: "20px"
    },
    courseItem: {
        width: "100%",
        height: "100%",
        maxWidth: "400px",
        borderRadius: "18px",
        border: "1px solid #d1d1d1"
    }
}));
const ProductRecommended = ({ onGoToItemAction, componentOverrides, ...props }) => {
    const { classes } = useStyles$3();
    return (jsxs(Box, { ...props, children: [jsx(Typography, { className: classes.title, variant: "h2", children: "You might also like" }), jsx(Box, { className: classes.productContainer, children: jsx(LibraryDataProvider, { search: { searchTerm: "" }, children: ({ items }) => jsx(Fragment, { children: items?.map((item, i) => (componentOverrides?.item?.({ item, onGoToItemAction, className: classes.courseItem }) ?? jsx(LibraryItem, { "data-test-id": `course-library-product-reccomend-option-${item.slug}`, item: item, className: classes.courseItem, onGoToItemAction: () => onGoToItemAction(item) }, `reccomended-course-${i}`))) }) }) })] }));
};

const useStyles$2 = makeStyles()((theme) => ({
    courseLibraryProductSummaryTile: {
        borderRadius: "10px",
        padding: "3px 0",
        background: theme.palette.background.paper,
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        "& > *": {
            padding: "20px 15px"
        }
    },
    topContainer: {
        "& > *": {
            marginBottom: "10px"
        },
        "& h4": {
            marginBottom: "5px"
        }
    },
    infoContainer: {
        borderTop: theme.palette.grey[200] + " 1px solid",
        borderBottom: theme.palette.grey[200] + " 1px solid",
        display: "flex",
        flexDirection: "column"
    },
    actionContainer: {
        "& button": {
            height: "52px",
            width: "100%"
        }
    },
    infoItem: {
        display: "flex",
        color: "#2A2A2F66",
        "& svg": {
            color: "#A8A8A880",
            marginRight: "8px"
        }
    }
}));
const CourseProductSummaryTile = ({ className, children, libraryItem, onEnrollAction, actionOverrideEnroll, ...props }) => {
    const { classes, cx } = useStyles$2();
    const { setEnroll, apiResult } = useSetCourseEnrollment();
    useEffect(() => {
        if (apiResult.statusCode === 200) {
            onEnrollAction ? onEnrollAction() : DevError("No enrollment action set");
        }
    }, [apiResult]);
    const handleEnrollment = () => {
        actionOverrideEnroll ? actionOverrideEnroll({ ...libraryItem, __type: "plugin::strapi-plugin-shapeshift-cms.course" }) : setEnroll(libraryItem.slug);
    };
    return (jsx(Box, { className: cx(classes.courseLibraryProductSummaryTile, className), ...props, children: children ? children : !libraryItem.enrolled ? jsxs(Fragment, { children: [jsxs(Box, { className: classes.topContainer, children: [jsx(Typography, { variant: "h4", children: "Enroll in this Course" }), jsx(Typography, { sx: { color: "#2A2A2F66" }, children: "You're eligible!" })] }), jsx(Box, { className: classes.actionContainer, children: jsx(Button, { "data-test-id": "course-library-product-summary-tile-enroll-in-course-btn", onClick: () => handleEnrollment(), children: "Enroll in Course" }) })] })
            : jsx(Box, { className: classes.topContainer, children: jsx(Typography, { variant: "h4", children: "You're enrolled in this Course!" }) }) }));
};

const useSetGroupEnrollment = () => {
    const [apiResult, getData] = useApi(null);
    const setEnroll = (courseSlug, config) => getData({
        ...(config ?? {}),
        url: API.groupEnroll(courseSlug),
        method: "POST"
    });
    return { response: apiResult.data, apiResult, setEnroll };
};

const EnrollInCollectionButton = ({ children, libraryItem, onEnrollAction, actionOverrideEnroll, ...props }) => {
    const { setEnroll, apiResult } = useSetGroupEnrollment();
    useEffect(() => {
        if (apiResult.statusCode === 200) {
            onEnrollAction ? onEnrollAction() : DevError("No enrollment action set");
        }
    }, [apiResult]);
    const handleEnrollment = () => {
        if (!libraryItem || apiResult.isLoading)
            return;
        actionOverrideEnroll ? actionOverrideEnroll({ ...libraryItem, __type: "plugin::strapi-plugin-shapeshift-cms.group" }) : setEnroll(libraryItem.slug);
    };
    return (jsx(Button, { "data-test-id": "library-product-summary-tile-enroll-in-group-btn", onClick: () => handleEnrollment(), ...props, children: (children && typeof children === "function")
            ? children({ apiResult })
            : ((children && isValidElement(children)) ? children : (apiResult.isLoading ? "Enrolling..." : "Enroll in Collection")) }));
};

const useStyles$1 = makeStyles()((theme) => ({
    groupLibraryProductSummaryTile: {
        borderRadius: "10px",
        padding: "3px 0",
        background: theme.palette.background.paper,
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        "& > *": {
            padding: "20px 15px"
        }
    },
    topContainer: {
        "& > *": {
            marginBottom: "10px"
        },
        "& h4": {
            marginBottom: "5px"
        }
    },
    infoContainer: {
        borderTop: theme.palette.grey[200] + " 1px solid",
        borderBottom: theme.palette.grey[200] + " 1px solid",
        display: "flex",
        flexDirection: "column"
    },
    actionContainer: {
        "& button": {
            height: "52px",
            width: "100%"
        }
    },
    infoItem: {
        display: "flex",
        color: "#2A2A2F66",
        "& svg": {
            color: "#A8A8A880",
            marginRight: "8px"
        }
    }
}));
const GroupProductSummaryTile = ({ className, children, libraryItem, onEnrollAction, actionOverrideEnroll, ...props }) => {
    const { classes, cx } = useStyles$1();
    return (jsx(Box, { className: cx(classes.groupLibraryProductSummaryTile, className), ...props, children: children ? children : !libraryItem?.enrolled ? jsxs(Fragment, { children: [jsxs(Box, { className: classes.topContainer, children: [jsx(Typography, { variant: "h4", children: "Enroll in this Collection" }), jsx(Typography, { sx: { color: "#2A2A2F66" }, children: "You're eligible!" })] }), jsx(Box, { className: classes.actionContainer, children: jsx(EnrollInCollectionButton, { libraryItem: libraryItem, onEnrollAction: onEnrollAction, actionOverrideEnroll: actionOverrideEnroll }) })] })
            : jsx(Box, { className: classes.topContainer, children: jsx(Typography, { variant: "h4", children: "You're enrolled in this Collection!" }) }) }));
};

const useStyles = makeStyles()((theme) => ({
    courseLibraryProductSummaryTile: {
        borderRadius: "10px",
        padding: "3px 0",
        background: theme.palette.background.paper,
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        "& > *": {
            padding: "20px 15px"
        }
    }
}));
const ProductSummaryTile = ({ className, children, libraryItem, onEnrollAction, actionOverrideEnroll, ...props }) => {
    const { classes, cx } = useStyles();
    const itemProps = { onEnrollAction, actionOverrideEnroll };
    return (jsx(Box, { className: cx(classes.courseLibraryProductSummaryTile, className), ...props, children: children ? children : jsxs(Fragment, { children: [libraryItem && isILibraryGroup(libraryItem) && jsx(GroupProductSummaryTile, { libraryItem: libraryItem, ...itemProps }), libraryItem && isILibraryCourse(libraryItem) && jsx(CourseProductSummaryTile, { libraryItem: libraryItem, ...itemProps })] }) }));
};

export { CourseProductSummaryTile, EnrollInCollectionButton, GroupProductSummaryTile, ProductRecommended, ProductSummaryTile };
